'use client'

import { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { AlertCircle, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

export default function TranslationWarningBanner() {
  const [showBanner, setShowBanner] = useState(false)
  const { t } = useTranslation('common')

  useEffect(() => {
    const getClassNameString = (element: Element): string => {
      const attrClass = element.getAttribute('class')
      if (typeof attrClass === 'string') return attrClass

      // Some elements (e.g. SVG) expose className as non-string objects.
      const maybeClassName = (element as Element & { className?: unknown }).className
      if (typeof maybeClassName === 'string') return maybeClassName
      if (
        typeof maybeClassName === 'object' &&
        maybeClassName !== null &&
        'baseVal' in maybeClassName
      ) {
        const baseVal = (maybeClassName as { baseVal?: unknown }).baseVal
        return typeof baseVal === 'string' ? baseVal : ''
      }

      return ''
    }

    // Check for translation artifacts after a short delay to let browser translation kick in
    const checkTranslation = () => {
      // Method 1: Check for Google Translate wrapper elements
      const hasGoogleTranslate = document.querySelector('font[class*="translated"]') ||
        document.documentElement.classList.contains('translated-ltr') ||
        document.documentElement.classList.contains('translated-rtl') ||
        document.querySelector('body.translated-ltr') ||
        document.querySelector('body.translated-rtl')

      // Method 2: Check for Firefox translation
      const hasFirefoxTranslation = document.querySelector('[data-moz-translated]')

      // Method 3: Check for generic translation wrapper
      const hasTranslationWrapper = document.querySelector('[translate="yes"]') ||
        document.querySelector('font[style*="vertical-align"]')

      // Method 4: Check html lang vs actual content language mismatch
      const htmlLang = document.documentElement.lang
      const hasLangMismatch = htmlLang &&
        (document.documentElement.getAttribute('class')?.includes('translated') ||
         document.body.getAttribute('class')?.includes('translated'))

      if (hasGoogleTranslate || hasFirefoxTranslation || hasTranslationWrapper || hasLangMismatch) {
        setShowBanner(true)
      }
    }

    // Check immediately and after delay (translation can be async)
    const timeoutId = setTimeout(checkTranslation, 1000)

    // Also check on mutations (when translation happens dynamically)
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes') {
          const target = mutation.target as Element
          const className = getClassNameString(target)
          if (
            className.includes('translated') ||
            target.hasAttribute('data-moz-translated') ||
            target.tagName === 'FONT'
          ) {
            setShowBanner(true)
            observer.disconnect()
            break
          }
        }
      }
    })

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-moz-translated', 'lang'],
      subtree: true,
    })

    return () => {
      clearTimeout(timeoutId)
      observer.disconnect()
    }
  }, [])

  if (!showBanner) return null

  return (
    <Card
      className="fixed top-4 left-4 right-4 z-50 shadow-lg border-orange-500 bg-orange-50 text-orange-900 max-w-2xl mx-auto p-4"
      translate="no"
    >
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <strong className="font-semibold block">{t('translation_warning_title', 'Translation Detected')}</strong>
          <p className="text-sm mt-1">
            {t('translation_warning_message',
              'Browser auto-translation may prevent booking. Please disable translation for this page for the best experience.'
            )}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 flex-shrink-0 hover:bg-orange-100 -mt-1 -mr-1"
          onClick={() => setShowBanner(false)}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </Card>
  )
}
